import React, { useEffect, useRef, useState } from 'react';
import { useFlow } from '../utilities/useFlow';

type Status = 'idle' | 'loading' | 'loaded' | 'failed' | 'dismissed';

declare global {
  interface Window {
    Tawk_API?: {
      onLoad?: () => void;
      maximize?: () => void;
      [key: string]: unknown;
    };
    Tawk_LoadStart?: Date;
  }
}

const TAWK_SCRIPT_ID = 'tawk-to-widget';
const TAWK_BUBBLE_ID = 'tawk-bubble';
const LOAD_TIMEOUT_MS = 10000;
const SUPPORT_EMAIL = 'info@kitkeeper.co.uk';
const CONTACT_URL = 'https://www.kitkeeper.co.uk/contact';

function removeTawkArtifacts(): void {
  document.getElementById(TAWK_SCRIPT_ID)?.remove();
  document.getElementById(TAWK_BUBBLE_ID)?.remove();
  document
    .querySelectorAll<HTMLIFrameElement>('iframe[src*="tawk.to"]')
    .forEach((el) => el.remove());
}

export default function ChatLauncher(): JSX.Element | null {
  const flow = useFlow();
  const [status, setStatus] = useState<Status>('idle');
  const timeoutRef = useRef<number | null>(null);

  const propertyId = process.env.REACT_APP_TAWK_PROPERTY_ID;
  const widgetId = process.env.REACT_APP_TAWK_WIDGET_ID;
  const isKitKeeper = flow === 'Kit Keeper';

  useEffect(() => {
    if (!isKitKeeper) {
      removeTawkArtifacts();
      setStatus('idle');
    }
  }, [isKitKeeper]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!isKitKeeper || !propertyId || !widgetId) {
    return null;
  }

  if (status === 'loaded' || status === 'dismissed') {
    return null;
  }

  const clearLoadTimeout = () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleOpenChat = () => {
    if (status !== 'idle') return;

    if (document.getElementById(TAWK_SCRIPT_ID)) {
      window.Tawk_API?.maximize?.();
      setStatus('loaded');
      return;
    }

    setStatus('loading');

    // Tawk's loader extends window.Tawk_API rather than replacing it, so the
    // onLoad callback must be wired in before the script is appended.
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    window.Tawk_API.onLoad = () => {
      clearLoadTimeout();
      setStatus('loaded');
      window.Tawk_API?.maximize?.();
    };

    const script = document.createElement('script');
    script.id = TAWK_SCRIPT_ID;
    script.async = true;
    script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    script.onerror = () => {
      clearLoadTimeout();
      setStatus('failed');
    };

    timeoutRef.current = window.setTimeout(() => {
      setStatus((prev) => (prev === 'loaded' ? prev : 'failed'));
    }, LOAD_TIMEOUT_MS);

    document.body.appendChild(script);
  };

  if (status === 'failed') {
    return (
      <div
        className="chat-launcher chat-launcher--failed"
        role="status"
        aria-live="polite"
      >
        <button
          type="button"
          className="chat-launcher__dismiss"
          aria-label="Dismiss"
          onClick={() => setStatus('dismissed')}
        >
          ×
        </button>
        <p className="chat-launcher__title">Chat is unavailable right now</p>
        <p className="chat-launcher__body">
          Email us and we'll get back to you as soon as possible.
        </p>
        <a className="chat-launcher__link" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        <a
          className="chat-launcher__link chat-launcher__link--secondary"
          href={CONTACT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Visit contact page
        </a>
      </div>
    );
  }

  return (
    <div className="chat-launcher">
      <button
        type="button"
        className="chat-launcher__button"
        aria-label="Open chat"
        onClick={handleOpenChat}
        disabled={status === 'loading'}
      >
        {status === 'loading' ? (
          <span className="chat-launcher__spinner" aria-hidden="true" />
        ) : (
          <svg
            className="chat-launcher__icon"
            viewBox="0 0 24 24"
            width="26"
            height="26"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9.41l-3.7 3.7A1 1 0 0 1 4 20.29V6a2 2 0 0 1 2-2Zm3 6v2h10v-2H7Zm0 4v2h7v-2H7Z" />
          </svg>
        )}
      </button>
    </div>
  );
}
